/**
 * DependsOn Validation Service
 * 
 * Validates that fields with dependsOn or disabledDepends constraints
 * do not contain values when they should be hidden or disabled.
 * 
 * This ensures data integrity by preventing submission of invalid data
 * where hidden/disabled fields accidentally retain values.
 * 
 * Field Configuration Properties:
 * - skipDependsOnValidation: (boolean) Set to true to skip validation for this field.
 *   Useful for derived/calculated fields that may legitimately have values even when hidden.
 *   Example: { key: "calculatedField", skipDependsOnValidation: true, ... }
 * 
 * Error Display:
 * - Errors are set in Formik error structure and displayed on field components via helperText
 * - Fields are automatically marked as touched when validation fails so errors are visible
 * - Error messages appear on the field itself when rendered
 * 
 * Detailed Validation Result:
 * - Returns detailed information about problematic fields for better user feedback
 * - Includes field labels, paths, current values, and controlling field information
 * - Supports "Clear and Save" functionality by providing field paths for clearing
 */

import { getIn } from 'formik';

/**
 * @typedef {Object} ProblematicField
 * @property {string} fieldKey - The field's key
 * @property {string} fieldLabel - Human-readable field label
 * @property {string} fieldPath - Full path to the field (e.g., "category.field")
 * @property {string} categoryKey - Category key (null for root-level fields)
 * @property {string} categoryLabel - Category label (null for root-level fields)
 * @property {*} currentValue - Current value of the field
 * @property {string} constraintType - Type of constraint: 'visibility' or 'disabled'
 * @property {string} reason - Human-readable reason why the field is problematic
 * @property {Array<Object>} controllingFields - Fields that control the visibility/disabled state
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether validation passed
 * @property {Object} errors - Formik-compatible error object
 * @property {Array<ProblematicField>} problematicFields - Detailed info about problematic fields
 */

/**
 * Field types that should be skipped from validation (UI-only fields)
 */
const SKIP_VALIDATION_FIELDS = [
  'LABEL',
  'DATA_LABEL',
  'BUTTON',
  'DATA_HTML',
  'REMOVE_FIELD_BUTTON',
  'DOCUMENT_LINK',
  'VIDEO_LINK',
  'EMBED_VIDEO',
];

/**
 * UI-only field/category keys that should be skipped
 */
const SKIP_UI_KEYS = ['divider', 'testSpace'];

/**
 * Check if a controlling field value has a meaningful value (truthy check)
 * This properly handles special cases like:
 * - Boolean false (unchecked checkbox) → NOT a meaningful value
 * - Empty array [] (checkbox group with no selections) → NOT a meaningful value
 * - Boolean true (checked checkbox) → IS a meaningful value
 * - Non-empty array (checkbox group with selections) → IS a meaningful value
 * 
 * @param {*} value - The controlling field's value
 * @returns {boolean} - true if the value is meaningful, false otherwise
 */
const hasControllingFieldValue = (value) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return false;
  }

  // Handle empty string
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }

  // Handle boolean - only true is considered a meaningful value
  // This fixes the checkbox issue where false !== '' was returning true
  if (typeof value === 'boolean') {
    return value === true;
  }

  // Handle arrays (checkbox groups, multiselect) - only non-empty arrays are meaningful
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  // All other values (numbers including 0, non-empty strings) are considered meaningful
  return true;
};

/** Checkbox "checked" representations in form config vs DB (e.g. true vs "1"). */
const CHECKBOX_CHECKED_VALUES = [true, '1', 1, 'true'];

/**
 * Compare stored value to configured dependsValue.
 * In readonly mode, treat checkbox checked variants as equivalent.
 */
const valuesMatchForDependency = (actual, expected, readonly = false) => {
  if (actual === expected) {
    return true;
  }
  if (!readonly) {
    return false;
  }
  if (
    CHECKBOX_CHECKED_VALUES.includes(actual) &&
    CHECKBOX_CHECKED_VALUES.includes(expected)
  ) {
    return true;
  }
  return false;
};

const evaluateDependsOnFieldEntry = (dep, formValues, readonly) => {
  if (!dep || !dep.dependsOn) {
    return true;
  }

  const controllingValue = getIn(formValues, dep.dependsOn);

  if (dep.dependsValue !== undefined && dep.dependsValue !== null && dep.dependsValue !== '') {
    return valuesMatchForDependency(controllingValue, dep.dependsValue, readonly);
  }

  return hasControllingFieldValue(controllingValue);
};

/**
 * Same controlling path with multiple dependsValue entries = OR (e.g. true and "1").
 * Different paths = AND.
 */
const evaluateDependsOnFields = (dependsOnFields, formValues, readonly) => {
  const validDeps = dependsOnFields.filter((dep) => dep && dep.dependsOn);
  if (validDeps.length === 0) {
    return true;
  }

  const uniquePaths = new Set(validDeps.map((dep) => dep.dependsOn));
  const combiner = uniquePaths.size === 1 ? 'some' : 'every';

  return validDeps[combiner]((dep) => {
    try {
      return evaluateDependsOnFieldEntry(dep, formValues, readonly);
    } catch (error) {
      console.error('[dependsOnValidationService] checkFieldVisibility: Error checking dependency', {
        dependsOn: dep.dependsOn,
        error: error?.message,
      });
      return false;
    }
  });
};

/**
 * Check if a field should be visible based on dependsOn or dependsOnFields
 *
 * @param {Object} field - Field configuration object
 * @param {Object} formValues - Current form values
 * @param {boolean} readonly - When true, normalize checkbox DB values ("1") for visibility (read-only CRF view)
 * @returns {boolean} - true if field should be visible, false if hidden
 */
export const checkFieldVisibility = (field, formValues, readonly = false) => {
  if (!field || !formValues) {
    return true; // Default to visible if data is missing
  }

  if (Array.isArray(field.dependsOnFields) && field.dependsOnFields.length > 0) {
    return evaluateDependsOnFields(field.dependsOnFields, formValues, readonly);
  }

  if (field.dependsOn) {
    try {
      const controllingValue = getIn(formValues, field.dependsOn);
      if (field.dependsValue !== undefined && field.dependsValue !== null && field.dependsValue !== '') {
        return valuesMatchForDependency(controllingValue, field.dependsValue, readonly);
      }
      return hasControllingFieldValue(controllingValue);
    } catch (error) {
      console.error('[dependsOnValidationService] checkFieldVisibility: Error checking dependsOn', {
        dependsOn: field.dependsOn,
        error: error?.message,
      });
      return false;
    }
  }

  return true;
};

/**
 * Check if a field should be disabled based on disabledDepends or disabledDependsOnFields
 * 
 * @param {Object} field - Field configuration object
 * @param {Object} formValues - Current form values
 * @returns {boolean} - true if field should be disabled, false if enabled
 */
export const checkFieldDisabled = (field, formValues) => {
  if (!field || !formValues) {
    return false; // Default to enabled if data is missing
  }

  // If field is explicitly disabled, return true
  if (field.disabled === true) {
    return true;
  }

  // Check if disabledDependsOnFields array exists and has items
  if (Array.isArray(field.disabledDependsOnFields) && field.disabledDependsOnFields.length > 0) {
    // Check if any condition in the array is satisfied (OR logic - if any condition disables, field is disabled)
    return field.disabledDependsOnFields.some((dep) => {
      if (!dep || !dep.disabledDepends) {
        return false; // Skip invalid dependency items
      }

      try {
        // Handle nested paths (category.field) or flat paths (field)
        const dependsValue = dep.disabledDepends.includes('.')
          ? getIn(formValues, dep.disabledDepends)
          : formValues[dep.disabledDepends];

        // Check if disabledValidationValue is provided
        if (dep.disabledValidationValue !== undefined && dep.disabledValidationValue !== null) {
          return dependsValue === dep.disabledValidationValue;
        } else {
          return hasControllingFieldValue(dependsValue);
        }
      } catch (error) {
        console.error('[dependsOnValidationService] checkFieldDisabled: Error checking dependency', {
          disabledDepends: dep.disabledDepends,
          error: error?.message,
        });
        return false; // On error, assume field is enabled
      }
    });
  }

  // Fall back to single disabledDepends check for backward compatibility
  if (field.disabledDepends && field.disabledValidationValue !== undefined && field.disabledValidationValue !== null) {
    try {
      // Handle nested paths (category.field) or flat paths (field)
      const dependsKey = field.disabledDepends.includes('.')
        ? field.disabledDepends.split('.')
        : field.disabledDepends;

      const dependsValue = field.disabledDepends.includes('.')
        ? getIn(formValues, field.disabledDepends)
        : formValues[field.disabledDepends];

      return dependsValue === field.disabledValidationValue;
    } catch (error) {
      console.error('[dependsOnValidationService] checkFieldDisabled: Error checking disabledDepends', {
        disabledDepends: field.disabledDepends,
        error: error?.message,
      });
      return false; // On error, assume field is enabled
    }
  }

  // No dependency, field should be enabled
  return false;
};

/**
 * Check if a field value is considered "empty" based on field type
 * 
 * @param {*} value - Field value to check
 * @param {string} fieldType - Field type (e.g., 'CHECK_BOX', 'CHECK_BOX_GROUP', etc.)
 * @returns {boolean} - true if value is empty, false if it has a value
 */
export const isFieldValueEmpty = (value, fieldType) => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return true;
  }

  // Handle empty string
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }

  // Handle CHECK_BOX - false is considered empty
  if (fieldType === 'CHECK_BOX') {
    return value === false;
  }

  // Handle array fields (CHECK_BOX_GROUP, MULTISELECT_DROP_DOWN, SEARCH_DROP_DOWN)
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // Handle objects - empty object is considered empty
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value).length === 0;
  }

  // All other values (numbers, booleans true, strings with content) are considered non-empty
  return false;
};

/**
 * Check if a field should be skipped from validation
 * 
 * @param {Object} field - Field configuration object
 * @returns {boolean} - true if field should be skipped, false if it should be validated
 */
const shouldSkipField = (field) => {
  if (!field) {
    return true;
  }

  // Skip fields that explicitly opt out of dependsOn validation
  // Useful for derived/calculated fields that may have values even when hidden
  if (field.skipDependsOnValidation === true) {
    return true;
  }

  // Skip UI-only fields
  if (SKIP_VALIDATION_FIELDS.includes(field.type)) {
    return true;
  }

  // Skip UI-only keys
  if (SKIP_UI_KEYS.includes(field.key)) {
    return true;
  }

  // Skip fields with UI-only type
  if (field.type === 'divider' || field.type === 'testSpace') {
    return true;
  }

  return false;
};

/**
 * Check if a field has access based on user role
 * 
 * @param {Object} field - Field configuration object
 * @param {string} userSiteTrialRole - User's role
 * @returns {boolean} - true if user has access, false otherwise
 */
const hasFieldAccess = (field, userSiteTrialRole) => {
  if (!field || !field.access) {
    return true; // No access restriction, allow access
  }

  if (!userSiteTrialRole) {
    return false; // Field has access restriction but no user role
  }

  if (field.access === userSiteTrialRole) {
    return true;
  }

  if (Array.isArray(field.access) && field.access.includes(userSiteTrialRole)) {
    return true;
  }

  return false;
};

/**
 * Get controlling fields information for a field's visibility constraint
 * 
 * @param {Object} field - Field configuration object
 * @param {Object} formValues - Current form values
 * @returns {Array<Object>} - Array of controlling field information
 */
const getVisibilityControllingFields = (field, formValues) => {
  const controllingFields = [];

  if (Array.isArray(field.dependsOnFields) && field.dependsOnFields.length > 0) {
    field.dependsOnFields.forEach((dep) => {
      if (dep && dep.dependsOn) {
        const currentValue = getIn(formValues, dep.dependsOn);
        controllingFields.push({
          fieldPath: dep.dependsOn,
          requiredValue: dep.dependsValue,
          currentValue: currentValue,
          conditionMet: dep.dependsValue !== undefined && dep.dependsValue !== null && dep.dependsValue !== ''
            ? currentValue === dep.dependsValue
            : currentValue !== undefined && currentValue !== null && currentValue !== '',
        });
      }
    });
  } else if (field.dependsOn) {
    const currentValue = getIn(formValues, field.dependsOn);
    controllingFields.push({
      fieldPath: field.dependsOn,
      requiredValue: field.dependsValue,
      currentValue: currentValue,
      conditionMet: currentValue === field.dependsValue,
    });
  }

  return controllingFields;
};

/**
 * Validate visibility constraints for fields in a category or root level
 * 
 * @param {Array} fields - Array of field configurations
 * @param {Object} formValues - Current form values
 * @param {string|null} categoryKey - Category key (null for root-level fields)
 * @param {string|null} categoryLabel - Category label (null for root-level fields)
 * @param {string} userSiteTrialRole - User's role for access checking
 * @param {Object} errors - Error object to populate
 * @param {Array<ProblematicField>} problematicFields - Array to collect detailed field info
 * @returns {Object} - Updated errors object
 */
const validateVisibilityConstraints = (
  fields,
  formValues,
  categoryKey,
  categoryLabel,
  userSiteTrialRole,
  errors = {},
  problematicFields = [],
  parentIsHidden = false // NEW: Track if parent container is hidden
) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return errors;
  }

  for (const field of fields) {
    // Skip UI-only fields
    if (shouldSkipField(field)) {
      continue;
    }

    // Check field access
    if (!hasFieldAccess(field, userSiteTrialRole)) {
      continue; // Skip fields without access
    }

    // Check if field should be visible (considering parent visibility)
    const ownVisibility = checkFieldVisibility(field, formValues);
    const isVisible = parentIsHidden ? false : ownVisibility; // If parent is hidden, field is also hidden

    // Get field value
    const fieldPath = categoryKey ? `${categoryKey}.${field.key}` : field.key;
    const fieldValue = categoryKey ? getIn(formValues, fieldPath) : formValues[field.key];

    // If field is hidden and has a non-empty value, it's an error
    if (!isVisible && !isFieldValueEmpty(fieldValue, field.type)) {
      // Create descriptive error message that will be visible on the field
      const errorMessage = `This field must be empty because the condition to show it is not met. Please clear the value or satisfy the visibility condition.`;
      
      if (categoryKey) {
        if (!errors[categoryKey]) {
          errors[categoryKey] = {};
        }
        errors[categoryKey][field.key] = errorMessage;
      } else {
        errors[field.key] = errorMessage;
      }

      // Get controlling fields information
      const controllingFields = getVisibilityControllingFields(field, formValues);

      // Add detailed problematic field info
      problematicFields.push({
        fieldKey: field.key,
        fieldLabel: field.label || field.title || field.key,
        fieldPath: fieldPath,
        categoryKey: categoryKey,
        categoryLabel: categoryLabel,
        currentValue: fieldValue,
        constraintType: 'visibility',
        reason: `This field is hidden because the visibility condition is not met`,
        controllingFields: controllingFields,
        fieldType: field.type,
        subtype: field.subtype || null,
      });
    }

    // Recursively validate nested fields
    // IMPORTANT: Pass parent's hidden status to children - if parent is hidden, all children are hidden
    if (field.fields && Array.isArray(field.fields) && field.fields.length > 0) {
      validateVisibilityConstraints(
        field.fields,
        formValues,
        categoryKey, // Nested fields maintain the same category key
        categoryLabel,
        userSiteTrialRole,
        errors,
        problematicFields,
        !isVisible // Pass parent's hidden status to children
      );
    }
  }

  return errors;
};

/**
 * Get controlling fields information for a field's disabled constraint
 * 
 * @param {Object} field - Field configuration object
 * @param {Object} formValues - Current form values
 * @returns {Array<Object>} - Array of controlling field information
 */
const getDisabledControllingFields = (field, formValues) => {
  const controllingFields = [];

  if (Array.isArray(field.disabledDependsOnFields) && field.disabledDependsOnFields.length > 0) {
    field.disabledDependsOnFields.forEach((dep) => {
      if (dep && dep.disabledDepends) {
        const currentValue = getIn(formValues, dep.disabledDepends);
        controllingFields.push({
          fieldPath: dep.disabledDepends,
          disablingValue: dep.disabledValidationValue,
          currentValue: currentValue,
          causesDisabled: dep.disabledValidationValue !== undefined && dep.disabledValidationValue !== null
            ? currentValue === dep.disabledValidationValue
            : currentValue !== undefined && currentValue !== null && currentValue !== '',
        });
      }
    });
  } else if (field.disabledDepends) {
    const currentValue = getIn(formValues, field.disabledDepends);
    controllingFields.push({
      fieldPath: field.disabledDepends,
      disablingValue: field.disabledValidationValue,
      currentValue: currentValue,
      causesDisabled: currentValue === field.disabledValidationValue,
    });
  }

  return controllingFields;
};

/**
 * Validate disabled constraints for fields in a category or root level
 * 
 * @param {Array} fields - Array of field configurations
 * @param {Object} formValues - Current form values
 * @param {string|null} categoryKey - Category key (null for root-level fields)
 * @param {string|null} categoryLabel - Category label (null for root-level fields)
 * @param {string} userSiteTrialRole - User's role for access checking
 * @param {Object} errors - Error object to populate
 * @param {Array<ProblematicField>} problematicFields - Array to collect detailed field info
 * @returns {Object} - Updated errors object
 */
const validateDisabledConstraints = (
  fields,
  formValues,
  categoryKey,
  categoryLabel,
  userSiteTrialRole,
  errors = {},
  problematicFields = []
) => {
  if (!fields || !Array.isArray(fields) || fields.length === 0) {
    return errors;
  }

  for (const field of fields) {
    // Skip UI-only fields
    if (shouldSkipField(field)) {
      continue;
    }

    // Check field access
    if (!hasFieldAccess(field, userSiteTrialRole)) {
      continue; // Skip fields without access
    }

    // Check if field should be disabled
    const isDisabled = checkFieldDisabled(field, formValues);

    // Get field value
    const fieldPath = categoryKey ? `${categoryKey}.${field.key}` : field.key;
    const fieldValue = categoryKey ? getIn(formValues, fieldPath) : formValues[field.key];

    // If field is disabled and has a non-empty value, it's an error
    if (isDisabled && !isFieldValueEmpty(fieldValue, field.type)) {
      // Create descriptive error message that will be visible on the field
      const errorMessage = `This field must be empty because it's disabled. Please clear the value.`;
      
      // Check if this field is already in problematicFields (visibility error takes precedence)
      const alreadyExists = problematicFields.some(pf => pf.fieldPath === fieldPath);
      
      // Don't overwrite existing errors (visibility errors take precedence)
      const existingError = categoryKey
        ? errors[categoryKey]?.[field.key]
        : errors[field.key];

      if (!existingError && !alreadyExists) {
        if (categoryKey) {
          if (!errors[categoryKey]) {
            errors[categoryKey] = {};
          }
          errors[categoryKey][field.key] = errorMessage;
        } else {
          errors[field.key] = errorMessage;
        }

        // Get controlling fields information
        const controllingFields = getDisabledControllingFields(field, formValues);

        // Add detailed problematic field info
        problematicFields.push({
          fieldKey: field.key,
          fieldLabel: field.label || field.title || field.key,
          fieldPath: fieldPath,
          categoryKey: categoryKey,
          categoryLabel: categoryLabel,
          currentValue: fieldValue,
          constraintType: 'disabled',
          reason: `This field is disabled based on current form values`,
          controllingFields: controllingFields,
          fieldType: field.type,
          subtype: field.subtype || null,
        });
      }
    }

    // Recursively validate nested fields
    if (field.fields && Array.isArray(field.fields) && field.fields.length > 0) {
      validateDisabledConstraints(
        field.fields,
        formValues,
        categoryKey, // Nested fields maintain the same category key
        categoryLabel,
        userSiteTrialRole,
        errors,
        problematicFields
      );
    }
  }

  return errors;
};

/**
 * Main function to validate dependsOn and disabledDepends constraints
 * 
 * @param {Object} config - Form configuration object
 * @param {Object} formValues - Current form values
 * @param {Object} context - Validation context with userSiteTrialRole
 * @returns {ValidationResult} - { isValid: boolean, errors: {}, problematicFields: [] }
 */
export const validateDependsOnConstraints = (config, formValues, context = {}) => {
  if (!config || !formValues) {
    return { isValid: true, errors: {}, problematicFields: [] };
  }

  const { userSiteTrialRole } = context;
  const errors = {};
  const problematicFields = [];

  try {
    // Validate categories
    if (config.categories && Array.isArray(config.categories)) {
      for (const category of config.categories) {
        // Skip UI-only categories
        if (SKIP_UI_KEYS.includes(category.key)) {
          continue;
        }

        // Check category access
        if (category.access && userSiteTrialRole) {
          const hasAccess =
            category.access === userSiteTrialRole ||
            (Array.isArray(category.access) && category.access.includes(userSiteTrialRole));
          if (!hasAccess) {
            continue;
          }
        }

        // Check if category itself has dependsOn visibility constraint
        const categoryIsVisible = checkFieldVisibility(category, formValues);

        // Validate fields in category
        if (category.fields && Array.isArray(category.fields) && category.fields.length > 0) {
          const categoryLabel = category.label || category.title || category.key;

          // Validate visibility constraints - pass category visibility status
          validateVisibilityConstraints(
            category.fields,
            formValues,
            category.key,
            categoryLabel,
            userSiteTrialRole,
            errors,
            problematicFields,
            !categoryIsVisible // If category is hidden, all its fields are hidden
          );

          // Validate disabled constraints
          validateDisabledConstraints(
            category.fields,
            formValues,
            category.key,
            categoryLabel,
            userSiteTrialRole,
            errors,
            problematicFields
          );
        }
      }
    }

    // Validate root-level fields
    if (config.fields && Array.isArray(config.fields) && config.fields.length > 0) {
      // Validate visibility constraints
      validateVisibilityConstraints(
        config.fields,
        formValues,
        null,
        null,
        userSiteTrialRole,
        errors,
        problematicFields
      );

      // Validate disabled constraints
      validateDisabledConstraints(
        config.fields,
        formValues,
        null,
        null,
        userSiteTrialRole,
        errors,
        problematicFields
      );
    }

    const isValid = Object.keys(errors).length === 0;
    
    if (!isValid) {
      console.error('[dependsOnValidationService] validateDependsOnConstraints: Validation failed', {
        errorsCount: Object.keys(errors).length,
        problematicFieldsCount: problematicFields.length,
        errors,
        problematicFields,
      });
    }

    return { isValid, errors, problematicFields };
  } catch (error) {
    console.error('[dependsOnValidationService] validateDependsOnConstraints: Exception during validation', {
      error: error?.message,
      stack: error?.stack,
    });
    
    // On error, fail open (allow submission) - can be changed to fail closed
    // This prevents blocking users if there's a bug in validation logic
    return { isValid: true, errors: {}, problematicFields: [] };
  }
};

/**
 * Get the empty value for a field based on its type
 * Used for clearing problematic fields
 * 
 * @param {string} fieldType - The type of the field
 * @returns {*} - The appropriate empty value for the field type
 */
export const getEmptyValueForFieldType = (fieldType) => {
  switch (fieldType) {
    case 'CHECK_BOX':
      return false;
    case 'CHECK_BOX_GROUP':
    case 'MULTISELECT_DROP_DOWN':
    case 'SEARCH_DROP_DOWN':
      return [];
    case 'SLIDER':
      return 0;
    default:
      return '';
  }
};

